import { Component } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

// After a redeploy the old tab still asks for chunk hashes that no longer
// exist (serve -s answers them with index.html), so lazy pages/dialogs fail
// to load. A single reload picks up the new build; the timestamp guard stops
// a reload loop if the chunk is genuinely broken.
const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk .* failed|is not a valid JavaScript MIME type|Expected a JavaScript.*module script/i;
const RELOAD_KEY = "chunk-reload-at";

export function isChunkLoadError(error) {
  return CHUNK_ERROR.test(String(error?.message || error || ""));
}

export function reloadForNewBuild() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 10_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // storage blocked -- still worth one reload
  }
  window.location.reload();
  return true;
}

// One crashed widget should not white-screen a dashboard someone is using to
// run payroll. `resetKey` (e.g. the route path) clears the error when it
// changes, so navigating away from a crashed page actually shows the next one.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error) && reloadForNewBuild()) return;
    // Sentry.captureException(error, { extra: info }) in production.
    console.error("Render error:", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger/30 bg-danger-muted/40 p-8 text-center">
        <AlertTriangle className="h-6 w-6 text-danger" aria-hidden="true" />
        <p className="mt-3 text-sm font-medium text-foreground">This section could not be displayed</p>
        <p className="mt-1 text-[14px] text-muted-foreground">
          The rest of the page still works. Reloading usually clears it.
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    );
  }
}
